// KYC Test Data Generator for Validation Testing

export const generateTestIdentityDocument = () => {
  return {
    type: 'PAN_CARD',
    documentNumber: 'ABCDE1234F',
    data: {
      name: 'John Doe',
      fatherName: 'Robert Doe',
      dateOfBirth: '15/01/1990',
      panNumber: 'ABCDE1234F',
      issueDate: '01/01/2020',
      signature: true,
      photograph: true
    },
    authenticity: {
      isAuthentic: true,
      confidence: 0.95,
      tamperingDetected: false,
      qualityScore: 0.92,
      securityFeatures: {
        hologram: true,
        microtext: true,
        uvInk: true,
        embossedText: true,
        serialNumber: 'PAN123456789'
      }
    },
    expiry: {
      isValid: true,
      expiryDate: null, // PAN cards don't expire
      isPermanent: true
    }
  };
};

export const generateTestAddressDocument = () => {
  return {
    type: 'AADHAAR_CARD',
    documentNumber: '1234-5678-9012',
    data: {
      name: 'John Doe',
      address: '123 Test Street, Test Area, Test City, Test State - 123456',
      aadhaarNumber: '1234-5678-9012',
      dateOfBirth: '15/01/1990',
      gender: 'Male',
      issueDate: '01/06/2020',
      photograph: true
    },
    authenticity: {
      isAuthentic: true,
      confidence: 0.88,
      tamperingDetected: false,
      qualityScore: 0.85,
      securityFeatures: {
        hologram: true,
        microtext: true,
        qrCode: true,
        securityThread: true,
        serialNumber: 'ADH987654321'
      }
    },
    expiry: {
      isValid: true,
      expiryDate: null, // Aadhaar cards don't expire
      isPermanent: true
    },
    addressVerification: {
      pincode: '123456',
      city: 'Test City',
      state: 'Test State',
      country: 'India',
      addressType: 'Residential'
    }
  };
};

export const generateTestSelfieImage = () => {
  return {
    type: 'SELFIE',
    data: {
      timestamp: new Date().toISOString(),
      location: 'Test Location',
      deviceInfo: 'Test Device Camera',
      imageQuality: 'High',
      lighting: 'Good'
    },
    faceAnalysis: {
      faceDetected: true,
      faceCount: 1,
      faceQuality: 0.91,
      eyesOpen: true,
      mouthClosed: true,
      frontFacing: true,
      livenessScore: 0.87,
      spoofingDetected: false
    }
  };
};

export const generateTestBankDocument = () => {
  return {
    type: 'CANCELLED_CHEQUE',
    data: {
      accountHolderName: 'John Doe',
      accountNumber: '1234567890123456',
      ifscCode: 'SBIN0001234',
      bankName: 'State Bank of India',
      branchName: 'Test Branch',
      chequeNumber: '000001',
      micr: '123456789'
    },
    authenticity: {
      isAuthentic: true,
      confidence: 0.89,
      tamperingDetected: false,
      qualityScore: 0.87,
      securityFeatures: {
        watermark: true,
        micrLine: true,
        bankSeal: true,
        cancelledStamp: true
      }
    }
  };
};

export const generateTestGSTDocument = () => {
  return {
    type: 'GST_CERTIFICATE',
    documentNumber: '22AAAAA0000A1Z5',
    data: {
      legalName: 'John Doe Business Pvt Ltd',
      tradeName: 'John Doe Business',
      gstin: '22AAAAA0000A1Z5',
      registrationDate: '01/04/2020',
      constitutionOfBusiness: 'Private Limited Company',
      address: '123 Test Street, Test City, Test State - 123456',
      stateCode: '22',
      panNumber: 'ABCDE1234F'
    },
    authenticity: {
      isAuthentic: true,
      confidence: 0.93,
      tamperingDetected: false,
      qualityScore: 0.90,
      securityFeatures: {
        digitalSignature: true,
        qrCode: true,
        officialSeal: true,
        serialNumber: 'GST123456789'
      }
    },
    status: {
      isActive: true,
      lastUpdated: '2024-01-01',
      complianceStatus: 'Regular'
    }
  };
};

// Test scenarios for different validation outcomes
export const getTestScenarios = () => {
  return {
    // Scenario 1: Perfect Match - Should APPROVE
    perfectMatch: {
      identity: generateTestIdentityDocument(),
      address: generateTestAddressDocument(),
      selfie: generateTestSelfieImage(),
      bank: generateTestBankDocument(),
      gst: generateTestGSTDocument(),
      expectedOutcome: 'APPROVED',
      expectedScore: 95
    },

    // Scenario 2: Good Match with Minor Issues - Should MANUAL_REVIEW
    goodMatchWithIssues: {
      identity: {
        ...generateTestIdentityDocument(),
        authenticity: {
          ...generateTestIdentityDocument().authenticity,
          confidence: 0.82,
          qualityScore: 0.78
        }
      },
      address: generateTestAddressDocument(),
      selfie: {
        ...generateTestSelfieImage(),
        faceAnalysis: {
          ...generateTestSelfieImage().faceAnalysis,
          livenessScore: 0.75
        }
      },
      expectedOutcome: 'MANUAL_REVIEW',
      expectedScore: 78
    },

    // Scenario 3: Poor Quality Documents - Should REJECT
    poorQuality: {
      identity: {
        ...generateTestIdentityDocument(),
        authenticity: {
          ...generateTestIdentityDocument().authenticity,
          confidence: 0.45,
          qualityScore: 0.32,
          tamperingDetected: true
        }
      },
      address: {
        ...generateTestAddressDocument(),
        authenticity: {
          ...generateTestAddressDocument().authenticity,
          confidence: 0.38,
          qualityScore: 0.29
        }
      },
      expectedOutcome: 'REJECTED',
      expectedScore: 35
    },

    // Scenario 4: Data Inconsistency - Should REJECT
    dataInconsistency: {
      identity: generateTestIdentityDocument(),
      address: {
        ...generateTestAddressDocument(),
        data: {
          ...generateTestAddressDocument().data,
          name: 'Jane Smith', // Different name
          dateOfBirth: '20/05/1985' // Different DOB
        }
      },
      selfie: generateTestSelfieImage(),
      expectedOutcome: 'REJECTED',
      expectedScore: 42
    },

    // Scenario 5: Expired Documents - Should REJECT
    expiredDocuments: {
      identity: {
        ...generateTestIdentityDocument(),
        expiry: {
          isValid: false,
          expiryDate: '2023-01-01',
          isPermanent: false,
          isExpired: true
        }
      },
      address: generateTestAddressDocument(),
      expectedOutcome: 'REJECTED',
      expectedScore: 38
    }
  };
};

// Face matching test data
export const getFaceMatchTestData = () => {
  return {
    highMatch: {
      matchScore: 0.94,
      isMatch: true,
      confidence: 0.91,
      faceDetected: true,
      qualityChecks: {
        lighting: 'excellent',
        resolution: 'high',
        angle: 'frontal',
        occlusion: 'none',
        blur: 'minimal'
      },
      livenessScore: 0.89,
      spoofingDetected: false
    },
    mediumMatch: {
      matchScore: 0.76,
      isMatch: true,
      confidence: 0.72,
      faceDetected: true,
      qualityChecks: {
        lighting: 'good',
        resolution: 'medium',
        angle: 'slight_angle',
        occlusion: 'minimal',
        blur: 'slight'
      },
      livenessScore: 0.71,
      spoofingDetected: false
    },
    lowMatch: {
      matchScore: 0.42,
      isMatch: false,
      confidence: 0.38,
      faceDetected: true,
      qualityChecks: {
        lighting: 'poor',
        resolution: 'low',
        angle: 'side_angle',
        occlusion: 'partial',
        blur: 'significant'
      },
      livenessScore: 0.45,
      spoofingDetected: true
    }
  };
};

// Data consistency test patterns
export const getDataConsistencyPatterns = () => {
  return {
    perfectConsistency: {
      nameMatch: {
        identity: 'John Doe',
        address: 'John Doe',
        bank: 'John Doe',
        consistency: 1.0
      },
      addressMatch: {
        identity: '123 Test Street, Test City, 123456',
        address: '123 Test Street, Test City, 123456',
        consistency: 1.0
      },
      dobMatch: {
        identity: '15/01/1990',
        address: '15/01/1990',
        consistency: 1.0
      }
    },
    minorInconsistency: {
      nameMatch: {
        identity: 'John Doe',
        address: 'JOHN DOE',
        bank: 'John Doe',
        consistency: 0.95 // Case difference
      },
      addressMatch: {
        identity: '123 Test St, Test City, 123456',
        address: '123 Test Street, Test City, 123456',
        consistency: 0.92 // Abbreviation difference
      }
    },
    majorInconsistency: {
      nameMatch: {
        identity: 'John Doe',
        address: 'Jane Smith',
        bank: 'John Doe',
        consistency: 0.15 // Completely different names
      },
      dobMatch: {
        identity: '15/01/1990',
        address: '20/05/1985',
        consistency: 0.0 // Different dates
      }
    }
  };
};

// Document authenticity test patterns
export const getAuthenticityTestPatterns = () => {
  return {
    authentic: {
      confidence: 0.95,
      tamperingDetected: false,
      qualityScore: 0.92,
      securityFeatures: {
        hologram: true,
        microtext: true,
        uvInk: true,
        watermark: true,
        serialNumber: true
      },
      digitalSignature: true,
      issuerVerification: true
    },
    suspicious: {
      confidence: 0.65,
      tamperingDetected: true,
      qualityScore: 0.58,
      securityFeatures: {
        hologram: false,
        microtext: true,
        uvInk: false,
        watermark: true,
        serialNumber: false
      },
      digitalSignature: false,
      issuerVerification: false
    },
    fake: {
      confidence: 0.25,
      tamperingDetected: true,
      qualityScore: 0.31,
      securityFeatures: {
        hologram: false,
        microtext: false,
        uvInk: false,
        watermark: false,
        serialNumber: false
      },
      digitalSignature: false,
      issuerVerification: false
    }
  };
};

// Status transition test data
export const getStatusTransitionTests = () => {
  return {
    pendingToApproved: {
      from: 'PENDING_VALIDATION',
      to: 'APPROVED',
      score: 92,
      reason: 'All validation checks passed with high confidence',
      autoApproved: true,
      reviewRequired: false
    },
    pendingToManualReview: {
      from: 'PENDING_VALIDATION',
      to: 'MANUAL_REVIEW',
      score: 76,
      reason: 'Some validation checks require manual verification',
      autoApproved: false,
      reviewRequired: true
    },
    pendingToRejected: {
      from: 'PENDING_VALIDATION',
      to: 'REJECTED',
      score: 34,
      reason: 'Multiple validation checks failed',
      autoApproved: false,
      reviewRequired: false
    },
    manualReviewToApproved: {
      from: 'MANUAL_REVIEW',
      to: 'APPROVED',
      score: 85,
      reason: 'Manual review completed successfully',
      autoApproved: false,
      reviewRequired: false,
      reviewerComments: 'Documents verified manually, all checks passed'
    },
    manualReviewToRejected: {
      from: 'MANUAL_REVIEW',
      to: 'REJECTED',
      score: 45,
      reason: 'Manual review identified issues',
      autoApproved: false,
      reviewRequired: false,
      reviewerComments: 'Document authenticity could not be verified'
    }
  };
};

export default {
  generateTestIdentityDocument,
  generateTestAddressDocument,
  generateTestSelfieImage,
  generateTestBankDocument,
  generateTestGSTDocument,
  getTestScenarios,
  getFaceMatchTestData,
  getDataConsistencyPatterns,
  getAuthenticityTestPatterns,
  getStatusTransitionTests
};